import { FunctionComponent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { ComponentException } from '../../errors/ComponentException';
import { showMessage } from '../../features/message/message-slice';
import {
  loginUsingSSOCodeCentralAuth,
  repairSSOShadowCentralAuth,
} from '../../features/sign-in/services/direct-auth';
import { useCheckInstanceConfigMutation } from '../../features/sign-in/sign-in-api-slice';
import {
  setSelectedInstance,
  setUsername,
  updateSignInState,
} from '../../features/sign-in/sign-in-slice';
import { SignInStepType } from '../../features/sign-in/types/SignInStepType';
import { logError } from '../../helper/LoggerHelper';
import { handleLoginResponse } from '../../helper/LoginHelper';
import { redirect } from '../../helper/RedirectHelper';
import { getMessagesFromResponseError } from '../../helper/request-helper';

const PROVIDER_USERNAME_MISMATCH = 'Invalid ProviderName/Username combination';

type Props = {
  children: any;
};

export const CallbackPage: FunctionComponent<Props> = (props) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const stateId = searchParams.get('state');
  const instance = searchParams.get('instance');
  const errorDescription = searchParams.get('error_description');
  const { i18n } = useTranslation();
  const [checkInstanceConfig] = useCheckInstanceConfigMutation();
  // StrictMode runs the effect twice in development; a second repair would be
  // refused by central_auth and show an error over the running redirect.
  const shadowRepairStarted = useRef(false);

  const loadTargetInstance = async (host: string) => {
    console.debug('Loading environments...');

    const targetInstance = await checkInstanceConfig({ host }).unwrap();

    console.debug(`Target instance: ${JSON.stringify(targetInstance)}`);

    if (!targetInstance) {
      throw new ComponentException(`Instance is not found by host: ${host}`);
    }

    return targetInstance;
  };

  // Cognito rejects the SAML response before central_auth sees it when the
  // user's shadow holds the NameID in another casing (KOALA-1635). Repairing
  // deletes the shadow and starts one more login; central_auth refuses a
  // second repair for the same login.
  const tryShadowRepair = async (): Promise<boolean> => {
    if (
      !stateId ||
      !instance ||
      !errorDescription?.includes(PROVIDER_USERNAME_MISMATCH)
    ) {
      return false;
    }

    if (shadowRepairStarted.current) {
      return true;
    }
    shadowRepairStarted.current = true;

    try {
      const targetInstance = await loadTargetInstance(instance);
      const { idpUrl } = await repairSSOShadowCentralAuth(
        targetInstance.host,
        stateId,
        i18n.language
      );

      if (!idpUrl?.startsWith('https://')) {
        throw new ComponentException(`Unexpected SSO repair URL: ${idpUrl}`);
      }

      redirect(idpUrl);

      return true;
    } catch (repairError) {
      logError(repairError);

      return false;
    }
  };

  const handleParameters = async () => {
    if (errorDescription) {
      if (await tryShadowRepair()) {
        return;
      }

      throw new ComponentException(errorDescription);
    }

    if (!stateId || !code || !instance) {
      throw new ComponentException('state and code and instance are required.');
    }

    const targetInstance = await loadTargetInstance(instance);

    dispatch(setSelectedInstance(targetInstance));

    const data = await loginUsingSSOCodeCentralAuth(
      instance,
      code,
      stateId,
      i18n.language
    );

    if (data.challenge && data.challenge.username) {
      dispatch(setUsername(data.challenge.username));
    }

    handleLoginResponse(data, dispatch);
  };

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(updateSignInState(SignInStepType.Initialization));

    handleParameters().catch((e) => {
      logError(e);
      const errorMessages: string[] = getMessagesFromResponseError(e);

      dispatch(
        showMessage({
          type: 'error',
          messages: errorMessages,
        })
      );

      navigate(`/${i18n.language}/sso`, { replace: true });
    });
  }, [code, stateId]);

  return props.children;
};
